import React, { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { ArrowRight, ChevronDown } from "lucide-react"

const slides = [
  {
    id: 1,
    image: "https://images.unsplash.com/photo-1486325212027-8081e485255e?w=1800&q=80&fit=crop",
    category: "Architecture",
    title: "Where Form\nMeets Purpose",
    subtitle: "Redefining built environments through disciplined aesthetics and structural mastery.",
    project: "The Meridian Residences — Dubai",
  },
  {
    id: 2,
    image: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1800&q=80&fit=crop",
    category: "Interior Design",
    title: "Spaces That\nTell Stories",
    subtitle: "Curated interiors where material, light, and form create immersive experiences.",
    project: "Lumière Penthouse — New York",
  },
  {
    id: 3,
    image: "https://images.unsplash.com/photo-1487958449943-2429e8be8625?w=1800&q=80&fit=crop",
    category: "3D Visualization",
    title: "Visions Before\nThey Exist",
    subtitle: "Cinematic renderings that bridge imagination and architectural reality.",
    project: "Solaris Tower — Singapore",
  },
]

export default function Hero() {
  const [current, setCurrent] = useState(0)
  const [transitioning, setTransitioning] = useState(false)

  useEffect(() => {
    const timer = setInterval(() => {
      setTransitioning(true)
      setTimeout(() => {
        setCurrent((prev) => (prev + 1) % slides.length)
        setTransitioning(false)
      }, 600)
    }, 6000)
    return () => clearInterval(timer)
  }, [])

  const goTo = (i) => {
    if (i === current) return
    setTransitioning(true)
    setTimeout(() => {
      setCurrent(i)
      setTransitioning(false)
    }, 400)
  }

  const slide = slides[current]

  return (
    <section className="relative w-full h-screen min-h-[600px] overflow-hidden">
      {/* Background Images */}
      {slides.map((s, i) => (
        <div
          key={s.id}
          className={`absolute inset-0 transition-all duration-1000 ${
            i === current ? "opacity-100 scale-100" : "opacity-0 scale-105"
          }`}
          style={{ zIndex: i === current ? 1 : 0 }}
        >
          <img
            src={s.image}
            alt={s.title}
            className="w-full h-full object-cover"
            loading={i === 0 ? "eager" : "lazy"}
          />
        </div>
      ))}

      {/* Gradient Overlays */}
      <div className="absolute inset-0 z-10 bg-gradient-to-r from-stone-950/85 via-stone-950/50 to-transparent" />
      <div className="absolute inset-0 z-10 bg-gradient-to-t from-stone-950/60 via-transparent to-stone-950/20" />

      {/* Content */}
      <div className="relative z-20 h-full flex items-center">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-12 w-full">
          <div className="max-w-2xl">
            {/* Category tag */}
            <div
              className={`transition-all duration-700 ${
                transitioning ? "opacity-0 translate-y-4" : "opacity-100 translate-y-0"
              }`}
            >
              <span className="inline-flex items-center gap-2 font-['Space_Grotesk'] text-[10px] tracking-[0.3em] text-amber-400 uppercase mb-6">
                <span className="w-8 h-px bg-amber-400" />
                {slide.category}
              </span>
            </div>

            {/* Title */}
            <h1
              className={`font-['Cormorant_Garamond'] text-5xl sm:text-6xl lg:text-8xl font-light text-stone-50 leading-[1.05] tracking-tight mb-6 whitespace-pre-line transition-all duration-700 delay-100 ${
                transitioning ? "opacity-0 translate-y-6" : "opacity-100 translate-y-0"
              }`}
            >
              {slide.title}
            </h1>

            {/* Subtitle */}
            <p
              className={`font-['Inter'] text-base lg:text-lg text-stone-300 leading-relaxed mb-10 max-w-lg transition-all duration-700 delay-200 ${
                transitioning ? "opacity-0 translate-y-6" : "opacity-100 translate-y-0"
              }`}
            >
              {slide.subtitle}
            </p>

            {/* CTAs */}
            <div
              className={`flex flex-col sm:flex-row gap-4 transition-all duration-700 delay-300 ${
                transitioning ? "opacity-0 translate-y-6" : "opacity-100 translate-y-0"
              }`}
            >
              <Link
                to="/works"
                className="inline-flex items-center gap-3 bg-amber-400 text-stone-950 px-8 py-4 font-['Space_Grotesk'] text-xs tracking-[0.2em] font-medium hover:bg-amber-300 transition-colors group"
              >
                EXPLORE WORKS
                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-3 border border-stone-400/40 text-stone-200 px-8 py-4 font-['Space_Grotesk'] text-xs tracking-[0.2em] font-medium hover:border-stone-200 hover:text-stone-100 transition-colors backdrop-blur-sm"
              >
                START A PROJECT
              </Link>
            </div>

            {/* Current project label */}
            <div
              className={`mt-12 transition-all duration-700 delay-400 ${
                transitioning ? "opacity-0" : "opacity-100"
              }`}
            >
              <p className="font-['Space_Grotesk'] text-[10px] tracking-[0.25em] text-stone-500 uppercase">
                Featured — {slide.project}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Slide Indicators */}
      <div className="absolute bottom-12 right-8 lg:right-12 z-20 flex flex-col gap-2.5">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className={`transition-all duration-300 ${
              i === current
                ? "w-px h-10 bg-amber-400"
                : "w-px h-5 bg-stone-600 hover:bg-stone-400"
            }`}
          />
        ))}
      </div>

      {/* Slide Counter */}
      <div className="absolute bottom-12 left-6 lg:left-12 z-20">
        <span className="font-['Space_Grotesk'] text-xs text-stone-500 tracking-widest">
          0{current + 1} / 0{slides.length}
        </span>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2 animate-bounce">
        <ChevronDown size={16} className="text-stone-500" />
      </div>
    </section>
  )
}
